import React from "react";

import { formatTime } from "../Helpers/formatTime";
import { ChatMessage } from "../Types/BotChat.Types";
import { BotAvatar } from "./BotAvatar";

// Same generic fallback the v2 widget's TakeoverEvent used.
const GENERIC_AVATAR = "https://swivl-cdn.s3-us-west-1.amazonaws.com/avatar-generic.png";

/** Discriminator: does this message render as a takeover notice? */
export function isTakeoverEventMessage(message: ChatMessage): boolean {
  return (message.metadata as { isTakeoverEvent?: boolean } | undefined)?.isTakeoverEvent === true;
}

export interface AgentRef {
  memberId: number;
  name: string;
}

/**
 * Structured agent references carried in event metadata (ENG-2946) — either
 * the single agentMemberId/agentName pair most escalation events carry, or an
 * `agents: [{memberId, name}]` array for multi-agent events (pull takeover).
 */
export function agentRefsFromMetadata(metadata: Record<string, unknown> | undefined): AgentRef[] {
  const meta = (metadata ?? {}) as {
    agentMemberId?: unknown;
    agentName?: unknown;
    agents?: unknown;
  };
  const out: AgentRef[] = [];
  if (typeof meta.agentMemberId === "number" && typeof meta.agentName === "string") {
    out.push({ memberId: meta.agentMemberId, name: meta.agentName });
  }
  if (Array.isArray(meta.agents)) {
    for (const a of meta.agents) {
      const ref = a as { memberId?: unknown; name?: unknown };
      if (typeof ref?.memberId === "number" && typeof ref?.name === "string") {
        out.push({ memberId: ref.memberId, name: ref.name });
      }
    }
  }
  return out;
}

/**
 * Render `content` with every known agent name turned into a click target
 * (deep-links to the agent's dashboard profile). Longest names match first so
 * "Chuck Reina Jr" never half-matches "Chuck Reina". Plain text when no refs
 * or no handler.
 */
export function linkifyAgentNames(content: string, refs: AgentRef[], onAgentClick?: (memberId: number) => void): React.ReactNode {
  if (!onAgentClick || refs.length === 0) return content;
  const byLength = [...refs].sort((a, b) => b.name.length - a.name.length).filter((r) => r.name.trim().length > 0);
  if (byLength.length === 0) return content;

  const nodes: React.ReactNode[] = [];
  let rest = content;
  let key = 0;
  while (rest.length > 0) {
    let earliest: { idx: number; ref: AgentRef } | null = null;
    for (const ref of byLength) {
      const idx = rest.indexOf(ref.name);
      if (idx !== -1 && (earliest === null || idx < earliest.idx)) earliest = { idx, ref };
    }
    if (!earliest) {
      nodes.push(rest);
      break;
    }
    if (earliest.idx > 0) nodes.push(rest.slice(0, earliest.idx));
    const ref = earliest.ref;
    nodes.push(
      <button key={key++} type="button" className="cursor-pointer font-medium text-grey-900 underline" onClick={() => onAgentClick(ref.memberId)}>
        {ref.name}
      </button>
    );
    rest = rest.slice(earliest.idx + ref.name.length);
  }
  return nodes;
}

interface TakeoverEventNoticeProps {
  message: ChatMessage;
  /** When set, agent names deep-link to the agent's dashboard profile. */
  onAgentClick?: (memberId: number) => void;
  /**
   * Presentation.
   *   - "widget" (default): the quiet avatar-plus-line treatment. THIS IS
   *     SHIPPED TO CUSTOMERS — `MessageList` renders it inside the chat widget,
   *     so don't restyle it for an agent-console reason.
   *   - "console": the agent console's inline event pill, per Figma
   *     "Chat Invisibles" (node 72:7397) — teal-50 fill, 16px avatar,
   *     mono label with a wall-clock time.
   */
  variant?: "widget" | "console";
}

/**
 * Takeover notice — the v2 chat's TakeoverEvent design (ENG-2946): a small
 * round avatar next to a quiet grey line, NOT a chat bubble.
 *   - takeover started → the agent's avatar (generic fallback) +
 *     "<agent> has entered the conversation"
 *   - takeover ended   → the bot avatar + "You're now chatting with the bot"
 */
export function TakeoverEventNotice({ message, onAgentClick, variant = "widget" }: TakeoverEventNoticeProps): React.ReactElement {
  const meta = (message.metadata ?? {}) as { takeoverEnded?: boolean; agentAvatarURL?: string };
  const refs = agentRefsFromMetadata(message.metadata as Record<string, unknown> | undefined);
  const label = linkifyAgentNames(message.content, refs, onAgentClick);

  // Agent console — Figma "Chat Invisibles" pill. `body-mono` is safe in this
  // branch only: it never renders in the widget, which loads its own stylesheet
  // and would treat `.body-*` as inert.
  if (variant === "console") {
    const time = message.createdAt ? formatTime(message.createdAt) : "";
    return (
      // No vertical margin: the timeline already spaces its rows, and the extra
      // 8px each side made takeover pills sit noticeably further apart than the
      // internal rows around them.
      <div className="flex justify-center">
        {/* Stacked: the avatar sits centred above the sentence rather than
            leading it, so the line of text starts flush with the other rows in
            the timeline instead of being indented by an icon. */}
        <div className="body-mono inline-flex flex-col items-center gap-1 rounded-sm bg-teal-50 px-2 py-1 text-center text-grey-600">
          {meta.takeoverEnded ? (
            <BotAvatar />
          ) : (
            <img src={meta.agentAvatarURL || GENERIC_AVATAR} alt="" className="size-6 shrink-0 rounded-full object-cover" />
          )}
          <span>
            {label}
            {time && ` • ${time}`}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="my-2 flex items-center justify-center gap-2">
      {meta.takeoverEnded ? (
        <BotAvatar />
      ) : (
        <img src={meta.agentAvatarURL || GENERIC_AVATAR} alt="" className="h-5 w-5 shrink-0 rounded-full object-cover" />
      )}
      <span className="text-sm leading-4 text-grey-600">{label}</span>
    </div>
  );
}
